import { lazy, Suspense, useState } from "react";
import { Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * The lightbulb in the header: feedback in two taps, from wherever the reader
 * noticed the thing. Ported from PropPay LX; the panel itself is
 * FeedbackPanel, loaded on first use so it stays out of the first-load budget.
 */
const loadPanel = () => import("./FeedbackPanel");
const FeedbackPanel = lazy(loadPanel);

export function FeedbackButton({
  className,
  /**
   * The project the reader is looking at, if they are looking at one.
   *
   * An id, never a name. The server resolves the name from it — and only for a
   * caller who can already see that project — so nothing a browser can invent
   * reaches the subject line of our own inbox.
   */
  projectId,
}: {
  className?: string;
  projectId?: string | null;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  // Mounted from the first open and kept, so the panel's delayed reset — and
  // a draft reopened straight after a mis-click — behave as they always did.
  const [armed, setArmed] = useState(false);

  return (
    <>
      {/* The bell's shape: a 40px circle around an 18px glyph, with the halo
          taking it to 44px under a finger. */}
      <button
        type="button"
        aria-label={t("shellFeedbackSend", "Send Feedback")}
        aria-haspopup="dialog"
        data-testid="button-feedback"
        onPointerEnter={() => void loadPanel()}
        onFocus={() => void loadPanel()}
        onClick={() => {
          setArmed(true);
          setOpen(true);
        }}
        className={cn(
          "hit-target flex size-10 shrink-0 items-center justify-center rounded-full text-foreground/70",
          "transition-colors duration-instant ease-standard hover:bg-foreground/[.06] hover:text-foreground",
          "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
      >
        <Lightbulb className="size-[18px]" aria-hidden="true" />
      </button>

      {armed && (
        <Suspense fallback={null}>
          <FeedbackPanel open={open} onOpenChange={setOpen} projectId={projectId} />
        </Suspense>
      )}
    </>
  );
}
